import { Component, forwardRef, useEffect, useRef, useState, type ComponentPropsWithoutRef, type ReactNode } from "react";
import { Mic, MicOff } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";

type Profile = { voiceToTextEnabled?: boolean };

type SpeechRecognitionLike = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  onresult: ((event: any) => void) | null;
  onerror: ((event: any) => void) | null;
  onend: (() => void) | null;
};

function getSpeechRecognitionCtor(): (new () => SpeechRecognitionLike) | null {
  if (typeof window === "undefined") return null;
  const w = window as any;
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

export function useVoiceToTextEnabled() {
  const { data: profile } = useQuery<Profile>({
    queryKey: ["/api/profile"],
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
  return profile?.voiceToTextEnabled === true;
}

/**
 * Hook for wrapper components (VoiceToTextTextarea / VoiceToTextInput).
 * Calls onTranscript(final, interim) on each recognition event so the wrapper
 * can rebuild the field value from a fixed base snapshot captured at start.
 * Calls onRecognitionEnd() when recognition stops (naturally or explicitly)
 * so the wrapper can clear focused state if the field is no longer active.
 */
function useFieldSpeechRecognition(
  onTranscript: (finalText: string, interimText: string) => void,
  onRecognitionEnd: () => void,
) {
  const { toast } = useToast();
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const onTranscriptRef = useRef(onTranscript);
  const onEndRef = useRef(onRecognitionEnd);
  onTranscriptRef.current = onTranscript;
  onEndRef.current = onRecognitionEnd;
  const SpeechCtor = getSpeechRecognitionCtor();

  useEffect(() => {
    return () => {
      try { recognitionRef.current?.stop(); } catch {}
    };
  }, []);

  const startRecognition = () => {
    if (!SpeechCtor) return;
    const recognition = new SpeechCtor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = (typeof navigator !== "undefined" && navigator.language) || "en-US";

    let finalText = "";

    recognition.onresult = (event: any) => {
      finalText = "";
      let interimText = "";
      for (let i = 0; i < event.results.length; i++) {
        const r = event.results[i];
        if (r.isFinal) finalText += r[0].transcript;
        else interimText += r[0].transcript;
      }
      onTranscriptRef.current(finalText, interimText);
    };

    recognition.onerror = (event: any) => {
      const err = event?.error || "unknown";
      if (err === "not-allowed" || err === "service-not-allowed") {
        toast({
          title: "Microphone blocked",
          description: "Allow microphone access in your browser to use voice-to-text.",
          variant: "destructive",
        });
      } else if (err !== "aborted" && err !== "no-speech") {
        toast({ title: "Voice error", description: String(err), variant: "destructive" });
      }
      setListening(false);
    };

    recognition.onend = () => {
      setListening(false);
      recognitionRef.current = null;
      onEndRef.current();
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
      setListening(true);
    } catch (e: any) {
      toast({ title: "Could not start", description: e?.message || "Try again.", variant: "destructive" });
      setListening(false);
    }
  };

  const stop = () => {
    try { recognitionRef.current?.stop(); } catch {}
    setListening(false);
  };

  return { listening, startRecognition, stop, supported: !!SpeechCtor };
}

// ---------------------------------------------------------------------------
// Shared injection helper — writes `text` into an input/textarea from a
// captured base state so repeated interim-result events replace in-place
// rather than accumulating.
// ---------------------------------------------------------------------------
function makeFieldInjector(el: HTMLInputElement | HTMLTextAreaElement) {
  const insertAt = el.selectionStart ?? el.value.length;
  const baseValue = el.value;
  const before = baseValue.slice(0, insertAt);
  const after = baseValue.slice(insertAt);
  const sep = before.length > 0 && !/\s$/.test(before) ? " " : "";

  return (text: string) => {
    const next = before + sep + text + after;
    const proto =
      el.tagName === "TEXTAREA"
        ? HTMLTextAreaElement.prototype
        : HTMLInputElement.prototype;
    const setter = Object.getOwnPropertyDescriptor(proto, "value")?.set;
    setter?.call(el, next);
    el.dispatchEvent(new Event("input", { bubbles: true }));
    const caret = before.length + sep.length + text.length;
    try { el.setSelectionRange(caret, caret); } catch {}
  };
}

// ---------------------------------------------------------------------------
// VoiceFieldBoundary — localized error boundary so a runtime failure inside the
// voice layer (e.g. a future regression, a thrown hook, a missing browser API)
// degrades the field to a plain working input/textarea instead of throwing up to
// the global app boundary and blanking the whole page. The mic is an enhancement;
// its absence must never break text entry. Applied to all exported voice
// components below, so every importer on every surface inherits the fallback.
// ---------------------------------------------------------------------------
class VoiceFieldBoundary extends Component<
  { fallback: ReactNode; children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(error: unknown) {
    console.error("[voice-to-text] degraded to plain field:", error);
  }
  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

// ---------------------------------------------------------------------------
// VoiceToTextButton — inline mic placed next to a label or field.
// onTranscript is called ONCE per session with the accumulated final text
// (not on every interim event) so callers can safely append without
// duplicating partial results.
// ---------------------------------------------------------------------------
function VoiceToTextButtonImpl({
  onTranscript,
  className,
  "data-testid": testId = "button-voice-to-text",
}: {
  onTranscript: (text: string) => void;
  className?: string;
  "data-testid"?: string;
}) {
  const { toast } = useToast();
  const enabled = useVoiceToTextEnabled();
  const SpeechCtor = getSpeechRecognitionCtor();
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;
  const finalAccRef = useRef("");

  useEffect(() => {
    return () => { try { recognitionRef.current?.stop(); } catch {} };
  }, []);

  const start = () => {
    if (!SpeechCtor) return;
    finalAccRef.current = "";
    const recognition = new SpeechCtor();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = (typeof navigator !== "undefined" && navigator.language) || "en-US";

    recognition.onresult = (event: any) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) finalAccRef.current += event.results[i][0].transcript;
      }
    };

    recognition.onerror = (event: any) => {
      const err = event?.error || "unknown";
      if (err === "not-allowed" || err === "service-not-allowed") {
        toast({
          title: "Microphone blocked",
          description: "Allow microphone access in your browser to use voice-to-text.",
          variant: "destructive",
        });
      } else if (err !== "aborted" && err !== "no-speech") {
        toast({ title: "Voice error", description: String(err), variant: "destructive" });
      }
      setListening(false);
    };

    recognition.onend = () => {
      setListening(false);
      recognitionRef.current = null;
      const text = finalAccRef.current.trim();
      if (text) onTranscriptRef.current(text);
      finalAccRef.current = "";
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
      setListening(true);
    } catch (e: any) {
      toast({ title: "Could not start", description: e?.message || "Try again.", variant: "destructive" });
      setListening(false);
    }
  };

  const stop = () => {
    try { recognitionRef.current?.stop(); } catch {}
    setListening(false);
  };

  if (!enabled || !SpeechCtor) return null;

  return (
    <button
      type="button"
      tabIndex={-1}
      aria-label={listening ? "Stop voice-to-text" : "Start voice-to-text"}
      title={listening ? "Tap to stop" : "Tap to dictate"}
      data-testid={testId}
      onPointerDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (listening) stop(); else start();
      }}
      className={cn(
        "inline-flex items-center justify-center rounded-full transition-colors",
        "h-7 w-7 border border-border",
        listening
          ? "bg-red-600 text-white animate-pulse hover:bg-red-700"
          : "bg-background text-muted-foreground hover:bg-accent hover:text-foreground",
        className,
      )}
    >
      {listening ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
    </button>
  );
}

export function VoiceToTextButton(
  props: ComponentPropsWithoutRef<typeof VoiceToTextButtonImpl>,
) {
  return (
    <VoiceFieldBoundary fallback={null}>
      <VoiceToTextButtonImpl {...props} />
    </VoiceFieldBoundary>
  );
}

// ---------------------------------------------------------------------------
// VoiceToTextTextarea — drop-in replacement for <Textarea>. Shows a mic icon
// on the right edge when the field is focused; hides on blur (unless actively
// listening). Interim results replace the previous interim in-place without
// accumulating. Mic visibility is cleared when recognition ends naturally.
// ---------------------------------------------------------------------------
const VoiceToTextTextareaImpl = forwardRef<
  HTMLTextAreaElement,
  ComponentPropsWithoutRef<typeof Textarea>
>(({ className, onFocus, onBlur, ...props }, forwardedRef) => {
  const enabled = useVoiceToTextEnabled();
  const innerRef = useRef<HTMLTextAreaElement | null>(null);
  const [focused, setFocused] = useState(false);
  const injectRef = useRef<((text: string) => void) | null>(null);

  const handleRecognitionEnd = () => {
    injectRef.current = null;
    // Clear focused only if the underlying field is no longer the active element.
    if (innerRef.current && innerRef.current !== document.activeElement) {
      setFocused(false);
    }
  };

  const { listening, startRecognition, stop, supported } = useFieldSpeechRecognition(
    (final, interim) => {
      const combined = (final + interim).trim();
      if (combined && injectRef.current) injectRef.current(combined);
    },
    handleRecognitionEnd,
  );

  const showMic = enabled && supported && (focused || listening);

  const handleStart = () => {
    const el = innerRef.current;
    if (!el) return;
    injectRef.current = makeFieldInjector(el);
    startRecognition();
  };

  const handleStop = () => {
    stop();
    injectRef.current = null;
    if (innerRef.current && innerRef.current !== document.activeElement) {
      setFocused(false);
    }
  };

  return (
    <div className="relative">
      <Textarea
        ref={(el) => {
          innerRef.current = el;
          if (typeof forwardedRef === "function") forwardedRef(el);
          else if (forwardedRef) forwardedRef.current = el;
        }}
        className={cn(showMic ? "pr-8" : "", className)}
        onFocus={(e) => { setFocused(true); onFocus?.(e); }}
        onBlur={(e) => {
          if (!listening) setFocused(false);
          onBlur?.(e);
        }}
        {...props}
      />
      {showMic && (
        <button
          type="button"
          tabIndex={-1}
          aria-label={listening ? "Stop dictation" : "Dictate into this field"}
          title={listening ? "Stop dictation" : "Dictate into this field"}
          data-testid="button-inline-mic"
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (listening) handleStop(); else handleStart();
          }}
          className={cn(
            "absolute right-2 top-2 h-6 w-6 flex items-center justify-center rounded-full",
            "border border-border transition-colors",
            listening
              ? "bg-red-600 text-white animate-pulse hover:bg-red-700"
              : "bg-background text-muted-foreground hover:bg-accent hover:text-foreground",
          )}
        >
          {listening ? <MicOff className="h-3 w-3" /> : <Mic className="h-3 w-3" />}
        </button>
      )}
    </div>
  );
});
VoiceToTextTextareaImpl.displayName = "VoiceToTextTextareaImpl";

export const VoiceToTextTextarea = forwardRef<
  HTMLTextAreaElement,
  ComponentPropsWithoutRef<typeof Textarea>
>((props, ref) => (
  <VoiceFieldBoundary fallback={<Textarea ref={ref} {...props} />}>
    <VoiceToTextTextareaImpl ref={ref} {...props} />
  </VoiceFieldBoundary>
));
VoiceToTextTextarea.displayName = "VoiceToTextTextarea";

// ---------------------------------------------------------------------------
// VoiceToTextInput — drop-in replacement for <Input> with inline mic icon.
// ---------------------------------------------------------------------------
const VoiceToTextInputImpl = forwardRef<
  HTMLInputElement,
  ComponentPropsWithoutRef<typeof Input>
>(({ className, onFocus, onBlur, ...props }, forwardedRef) => {
  const enabled = useVoiceToTextEnabled();
  const innerRef = useRef<HTMLInputElement | null>(null);
  const [focused, setFocused] = useState(false);
  const injectRef = useRef<((text: string) => void) | null>(null);

  const handleRecognitionEnd = () => {
    injectRef.current = null;
    if (innerRef.current && innerRef.current !== document.activeElement) {
      setFocused(false);
    }
  };

  const { listening, startRecognition, stop, supported } = useFieldSpeechRecognition(
    (final, interim) => {
      const combined = (final + interim).trim();
      if (combined && injectRef.current) injectRef.current(combined);
    },
    handleRecognitionEnd,
  );

  const showMic = enabled && supported && (focused || listening);

  const handleStart = () => {
    const el = innerRef.current;
    if (!el) return;
    injectRef.current = makeFieldInjector(el);
    startRecognition();
  };

  const handleStop = () => {
    stop();
    injectRef.current = null;
    if (innerRef.current && innerRef.current !== document.activeElement) {
      setFocused(false);
    }
  };

  return (
    <div className="relative">
      <Input
        ref={(el) => {
          innerRef.current = el;
          if (typeof forwardedRef === "function") forwardedRef(el);
          else if (forwardedRef) forwardedRef.current = el;
        }}
        className={cn(showMic ? "pr-8" : "", className)}
        onFocus={(e) => { setFocused(true); onFocus?.(e); }}
        onBlur={(e) => {
          if (!listening) setFocused(false);
          onBlur?.(e);
        }}
        {...props}
      />
      {showMic && (
        <button
          type="button"
          tabIndex={-1}
          aria-label={listening ? "Stop dictation" : "Dictate into this field"}
          title={listening ? "Stop dictation" : "Dictate into this field"}
          data-testid="button-inline-mic-input"
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (listening) handleStop(); else handleStart();
          }}
          className={cn(
            "absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center rounded-full",
            "border border-border transition-colors",
            listening
              ? "bg-red-600 text-white animate-pulse hover:bg-red-700"
              : "bg-background text-muted-foreground hover:bg-accent hover:text-foreground",
          )}
        >
          {listening ? <MicOff className="h-3 w-3" /> : <Mic className="h-3 w-3" />}
        </button>
      )}
    </div>
  );
});
VoiceToTextInputImpl.displayName = "VoiceToTextInputImpl";

export const VoiceToTextInput = forwardRef<
  HTMLInputElement,
  ComponentPropsWithoutRef<typeof Input>
>((props, ref) => (
  <VoiceFieldBoundary fallback={<Input ref={ref} {...props} />}>
    <VoiceToTextInputImpl ref={ref} {...props} />
  </VoiceFieldBoundary>
));
VoiceToTextInput.displayName = "VoiceToTextInput";
